import * as currency from '@shopify/theme-currency';
import Cookies from 'js-cookie';

let loop54UserIdSet = false;
let loadingLoop54UserId = false;

const setOrderAttributes = async (consentReadyEvent = false) => {
  const Loop54UserId = Cookies.get('Loop54User');
  const { consent } = window?.Cookiebot || {};
  const marketingConsent = consent?.marketing;

  try {
    if (!Loop54UserId && consentReadyEvent) {
      // This will create the client using the Loop54User cookie if consent is granted.
      window.loop54Config.getLoop54Client();

      // Call the function again on the next tick if after the cookie is set.
      setTimeout(setOrderAttributes, 0);

      return;
    }
  } catch (error) {
    console.error('Error creating Loop client');

    return;
  }

  if (!marketingConsent || !Loop54UserId || loadingLoop54UserId || loop54UserIdSet) {
    return;
  }

  const body = JSON.stringify({
    attributes: {
      __loop54userId: Loop54UserId,
    },
  });

  try {
    loadingLoop54UserId = true;

    await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });

    loop54UserIdSet = true;
  } catch (error) {
    console.error('Error setting order attributes');
  } finally {
    loadingLoop54UserId = false;
  }
};

window.addEventListener('CookiebotOnConsentReady', () => {
  setOrderAttributes(true);
});

try {
  setOrderAttributes();
} catch (error) {
  console.error('Error setting order attributes');
}

/* Update cart items */
if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();

      this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
      this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value),
        0
      );

      this.debouncedOnChange = debounce((event) => {
        if (event.target != document.getElementById('Cart-note')) {
          this.onChange(event);
        }
      }, 300);

      this.addEventListener('quantityChange', this.debouncedOnChange.bind(this));
      this.updateProgressBar();
    }

    onChange(event) {
      this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
    }

    getSectionsToRender() {
      return [
        {
          id: 'main-cart-items',
          section: document.getElementById('main-cart-items').dataset.id,
          selector: '.js-cart-item-contents',
        },
        {
          id: 'main-cart-footer',
          section: document.getElementById('main-cart-footer').dataset.id,
          selector: '.js-cart-footer-contents',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '[data-cart-icon-bubble]',
        },
      ];
    }

    async updateQuantity(line, quantity, name) {
      // Set loading state on cart-items, prevent other events trigger
      this.enableLoading(line);

      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      try {
        const response = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          ...{ body },
        });
        const parsedState = await response.json();
        this.classList.toggle('cart-is-empty', parsedState.item_count === 0);
        this.renderContents(parsedState);
        this.updateLiveRegions(line, parsedState.item_count);
        document.getElementById(`CartItem-${line}`)?.querySelector(`[name="${name}"]`)?.focus();
        this.updateProgressBar(parsedState);
        this.disableLoading();
      } catch (error) {
        document.querySelector('[data-cart-loader-active="show"]')?.classList.add('hidden');
        document.querySelector('[data-cart-loader-active="hidden"]')?.classList.remove('hidden');
        document.getElementById('cart-errors').textContent = window.cartStrings.error;
        this.disableLoading();
      }
    }

    /**
     * renderContents
     * @param {Object} parsedState
     */
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section) => {
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      });
      window?.loyaltylion?.ui?.refresh?.();
    }

    /**
     * updateLiveRegions
     * @description Show error text on the cart items and line items.
     * @param {Number} line
     * @param {Number} itemCount
     */
    updateLiveRegions(line, itemCount) {
      if (this.currentItemCount === itemCount) {
        document.getElementById(`Line-item-error-${line}`).querySelector('.cart-item__error-text').innerHTML =
          window.cartStrings.quantityError.replace('[quantity]', document.getElementById(`Quantity-${line}`).value);
      }

      this.currentItemCount = itemCount;
      this.lineItemStatusElement.setAttribute('aria-hidden', true);

      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }

    /**
     * getSectionInnerHTML
     * @param {String} html
     * @param {String} selector
     * @returns {Nodes} DOM element.
     */
    getSectionInnerHTML(html, selector) {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    /**
     * enableLoading
     */
    enableLoading() {
      document.querySelector('cart-items').classList.add('cart-items-is-loading');
      document.querySelector('[data-cart-loader-active="show"]')?.classList.remove('hidden');
      document.querySelector('[data-cart-loader-active="hidden"]')?.classList.add('hidden');
      document.activeElement.blur();
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }

    /**
     * disableLoading
     */
    disableLoading() {
      document.querySelector('cart-items').classList.remove('cart-items-is-loading');
    }

    /**
     * updateProgressBar
     * @param {Object} parsedState
     * @returns {Void}
     */
    updateProgressBar(parsedState = null) {
      // Get the elements
      const progressBarElement = document.querySelector('cart-progress-bar');
      const progressBarValue = document.querySelector('[data-progress-bar-price]');
      const freeShippingElement = document.querySelector('[data-cart-total-free-shipping]');

      // CHecking if any of the line_items has custom shipping enabled
      const customShipping = this.querySelectorAll('[data-custom-shipping]');
      if (customShipping.length) {
        progressBarElement.classList.add('hidden');
      } else {
        progressBarElement.classList.remove('hidden');
      }

      // Check if the main elements are found
      if (!progressBarElement || !progressBarValue || !freeShippingElement || !parsedState) {
        return;
      }

      const runningText = document.querySelector('[data-free-shipping-running-text]');
      const reachedText = document.querySelector('[data-free-shipping-reached-text]');

      // Select percentage elements en get amount
      const priceBarPercentage = document.querySelector('[data-pricebar-percentage]');
      const freeShippingThreshold = parseInt(document.querySelector('[data-free-shipping-from-price]').dataset.freeShippingFromPrice);

      // Calculate percentage and update it in UI
      let percentage = (100 * parsedState.total_price) / freeShippingThreshold;
      percentage >= 100 ? (percentage = 100) : '';

      // Set price bar percentage
      priceBarPercentage.style.width = percentage + '%';

      let priceDifferenceInCent = freeShippingThreshold - parsedState.total_price;
      priceDifferenceInCent <= 0 ? (priceDifferenceInCent = 0) : '';

      const spendMoreMoney = currency.formatMoney(priceDifferenceInCent, shopCurrency); // render money amount
      progressBarValue.innerHTML = spendMoreMoney;

      // Toggle elements
      if (priceDifferenceInCent <= 0) {
        freeShippingElement.classList.add('active');
        runningText.classList.add('hidden');
        reachedText.classList.remove('hidden');
      } else {
        freeShippingElement.classList.remove('active');
        runningText.classList.remove('hidden');
        reachedText.classList.add('hidden');
      }
    }

    /**
     * refreshState
     * @description Fetches and replaces the cart-items web component completly.
     */
    async refreshState() {
      if (!document.querySelector('cart-items')) {
        return;
      }
      const sectionId = this.dataset.id;
      try {
        const response = await fetch(`/cart?section_id=${sectionId}`);
        const html = await response.text();
        const newCartItems = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-items').closest('.container__content').innerHTML;
        document.querySelector('cart-items').closest('.container__content').innerHTML = newCartItems;
      } catch (error) {
        console.log(error);
      }
    }
  }
  customElements.define('cart-items', CartItems);
}

/* Remove item from the cart  */
if (!customElements.get('cart-remove-button')) {
  class CartRemoveButton extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        event.preventDefault();
        this.closest('cart-items').updateQuantity(this.dataset.index, 0);
      });
    }
  }
  customElements.define('cart-remove-button', CartRemoveButton);
}

/* Update the cart note */
if (!customElements.get('cart-note')) {
  /**
   * CartNote
   * @description Updates the cart no
   */
  class CartNote extends HTMLElement {
    constructor() {
      super();
      this.addEventListener(
        'change',
        debounce((event) => {
          const body = JSON.stringify({ note: event.target.value });
          fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
        }, 300)
      );
    }
  }
  customElements.define('cart-note', CartNote);
}
